import React, { useState } from "react";
import DesignPreviewShell from "@/components/DesignPreviewShell";
import { useDesignTheme } from "@/hooks/useDesignTheme";

/**
 * Field Radar × ContractorPlus — Phase 1 Design Preview
 *
 * READ-ONLY VISUAL PROTOTYPE. Nothing is wired to real data. Nothing on real
 * admin pages is touched. Use this route to eyeball the new look and iterate
 * with the agent before shipping for real.
 *
 * Route: /admin/design-preview
 */
export default function DesignPreview() {
  const [ribbon, setRibbon] = useState("c"); // a=progress, b=next-step, c=none, d=quiet-link
  const { theme } = useDesignTheme();

  return (
    <DesignPreviewShell>
    <div className="font-sans">
      {/* ── HERO GRADIENT ────────────────────────────────────────── */}
      <div className={`${theme.gradient} pt-5 pb-20 sm:pt-8 sm:pb-28 lg:pt-10 lg:pb-40 px-4 sm:px-6 relative`}>
        <div className="max-w-7xl mx-auto space-y-3 sm:space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-2 sm:gap-3">
            <div className="min-w-0">
              <h1 className="text-white font-black text-2xl sm:text-4xl lg:text-5xl tracking-tight leading-tight" data-testid="dp-greeting">
                Good Afternoon, Andrew! 🍊
              </h1>
              <p className="text-white/80 text-[11px] sm:text-sm mt-1 italic max-w-xl">
                "Success is stumbling from failure to failure with no loss of enthusiasm." — W. Churchill
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-8 sm:h-9 px-2.5 sm:px-3 rounded-full bg-white/10 text-white/90 text-[10px] sm:text-xs font-black uppercase tracking-widest inline-flex items-center backdrop-blur-sm whitespace-nowrap">
                Sep 22 · 2:04 PM
              </span>
              <button className="h-8 sm:h-9 px-3 sm:px-4 rounded-full bg-[#00C4CC] hover:bg-[#00a8af] text-[#0F1F3A] text-[10px] sm:text-xs font-black uppercase tracking-widest whitespace-nowrap">
                + New WO
              </button>
            </div>
          </div>

          {/* Ribbon variants (pick your poison) */}
          {ribbon === "a" && (
            <div className="mt-4 bg-[#0F1F3A]/90 backdrop-blur p-2 pl-4 pr-2 rounded-full inline-flex items-center gap-4">
              <span className="text-white text-sm font-bold">🎯 Setup <b>8/35</b> · You're 23% there</span>
              <div className="w-40 h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#22C6A6] to-[#00C4CC]" style={{ width: "23%" }}/>
              </div>
              <span className="bg-white/10 text-white px-3 py-1 rounded-full text-xs font-bold hover:bg-white/20 cursor-pointer">Continue →</span>
            </div>
          )}
          {ribbon === "b" && (
            <div className="mt-4 bg-[#0F1F3A]/90 backdrop-blur px-4 py-2 rounded-full inline-flex items-center gap-3">
              <span className="text-white text-sm font-medium">✨ <b>Next:</b> Connect Twilio to unlock SMS alerts</span>
              <button className="text-[#FF6A2B] text-xs font-black hover:underline">Do it →</button>
              <button className="text-white/40 text-xs hover:text-white">Later</button>
            </div>
          )}
          {ribbon === "c" && null /* silent, no ribbon */}
          {ribbon === "d" && (
            <div className="mt-4 inline-flex items-center gap-2 text-white/60 text-xs font-medium hover:text-white cursor-pointer">
              <span>💡 Setup Tips (23)</span>
            </div>
          )}
        </div>
      </div>

      {/* ── FLOATING DASHBOARD ─────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-12 sm:-mt-20 lg:-mt-28 pb-16">
        {/* Ribbon picker (this is meta — not part of the final UI) */}
        <div className="mb-4 bg-white/5 border-2 border-dashed border-white/10 rounded-xl p-3">
          <div className="text-white/60 text-[10px] font-black uppercase tracking-widest mb-2">
            Onboarding ribbon variant:
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { k: "a", label: "A · Progress" },
              { k: "b", label: "B · Next step" },
              { k: "c", label: "C · None" },
              { k: "d", label: "D · Quiet link" },
            ].map(o => (
              <button key={o.k} onClick={() => setRibbon(o.k)}
                data-testid={`dp-ribbon-${o.k}`}
                className={`text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full ${
                  ribbon === o.k
                    ? "bg-[#00C4CC] text-[#0F1F3A]"
                    : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
                }`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-8 space-y-6">

            {/* Workflow Arc — ProCall pipeline */}
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-6" data-testid="dp-workflow-arc">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-black text-[#0F1F3A]">Your Pipeline</h3>
                  <p className="text-[#8B93A7] text-xs font-medium">Where every WO is right now</p>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">2,847 total</span>
              </div>
              <WorkflowArc/>
            </div>

            {/* Action Items */}
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-6" data-testid="dp-action-items">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-black text-[#0F1F3A]">Action Items</h3>
                <div className="flex gap-1 text-[10px] font-black uppercase tracking-widest">
                  {["All (14)", "WOs (8)", "Timesheets (3)", "Invoices (2)", "Alerts (1)"].map((t, i) => (
                    <button key={t} className={`px-2 py-1 rounded-full ${
                      i === 0 ? "bg-[#0F1F3A] text-white" : "text-[#8B93A7] hover:bg-slate-50"
                    }`}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                {[
                  { icon: "🔗", color: "#B12FBB", text: "Pull 5 WOs from ClickUp (3712-3716)", meta: "Sync Health · just now" },
                  { icon: "📦", color: "#22C6A6", text: "Parts arrived · WO 3711-1 · 1230 Anyon St", meta: "Jhan · 12 min ago" },
                  { icon: "⏱", color: "#F5B93F", text: "Approve Marcus timesheet · 8.5h", meta: "Timesheets · 1h ago" },
                  { icon: "❄️", color: "#3B82F6", text: "NWS: 4+\" snow Wed — trigger 12 landscaping WOs?", meta: "Snow Alert · 2h ago" },
                  { icon: "🔥", color: "#E64C5A", text: "WO 3618-1 flagged emergency — no tech assigned", meta: "Guardrail · 3h ago" },
                ].map((it, i) => (
                  <div key={i} data-testid={`dp-action-row-${i}`}
                    className="flex items-center gap-3 p-2.5 hover:bg-slate-50 rounded-lg group transition-colors">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center text-sm flex-shrink-0"
                      style={{ backgroundColor: it.color + "22" }}>
                      <span>{it.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-[#0F1F3A] truncate">{it.text}</div>
                      <div className="text-[10px] text-[#8B93A7] font-medium uppercase tracking-widest">{it.meta}</div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="h-7 px-2 rounded-full bg-[#00C4CC] text-[#0F1F3A] text-[10px] font-black uppercase tracking-widest hover:bg-[#00a8af]">
                        Action ⌄
                      </button>
                      <button className="h-7 px-2 rounded-full bg-[#22C6A6] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#1ba98d]">
                        ✓ Done
                      </button>
                      <button className="h-7 w-7 rounded-full text-[#8B93A7] hover:bg-slate-100 text-sm">×</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="lg:col-span-4 space-y-6">
            {/* Status donut */}
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-6" data-testid="dp-status-donut">
              <h3 className="text-xl font-black text-[#0F1F3A] mb-4">WO Status</h3>
              <div className="flex items-center justify-center py-2">
                <StatusDonut/>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                {[
                  { c: "#F5B93F", label: "Pending", n: 42 },
                  { c: "#FF6A2B", label: "In Progress", n: 18 },
                  { c: "#3B82F6", label: "Needs Photos", n: 7 },
                  { c: "#22C6A6", label: "Completed", n: 128 },
                  { c: "#E64C5A", label: "Emergency", n: 3 },
                  { c: "#8B93A7", label: "Archived", n: 619 },
                ].map(s => (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: s.c }}/>
                    <span className="text-[#8B93A7] font-medium truncate">{s.label}</span>
                    <span className="font-black text-[#0F1F3A] ml-auto">{s.n}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats pills */}
            <div className="grid grid-cols-2 gap-3" data-testid="dp-quick-stats">
              {[
                { label: "Today $", value: "$3,240", color: "#22C6A6", filter: "Rebilled ⌄" },
                { label: "WOs Today", value: "14", color: "#F5B93F", filter: "Closed ⌄" },
                { label: "Trucks Active", value: "3/4", color: "#00C4CC", filter: "Rolling ⌄" },
                { label: "Parts Arrived", value: "6", color: "#B12FBB", filter: "Today ⌄" },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <div className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">{s.label}</div>
                  <div className="text-2xl font-black mt-1" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-[9px] font-bold text-[#8B93A7] mt-1 hover:text-[#0F1F3A]">{s.filter}</div>
                </div>
              ))}
            </div>

            {/* Weather widget preview */}
            <div className="bg-gradient-to-br from-[#3B82F6] to-[#5B3AE0] rounded-2xl p-5 text-white shadow-[0_8px_30px_rgb(0,0,0,0.15)]" data-testid="dp-weather">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-white/70">Philadelphia · Now</div>
                  <div className="text-4xl font-black tracking-tight mt-1">64°F</div>
                  <div className="text-xs font-medium text-white/80">Patchy rain nearby</div>
                </div>
                <div className="text-5xl">🌦</div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                {[{ h: "3PM", i: "🌧", t: "63°" }, { h: "4PM", i: "☁️", t: "62°" }, { h: "5PM", i: "🌤", t: "64°" }].map(f => (
                  <div key={f.h} className="bg-white/10 backdrop-blur rounded-lg py-1.5">
                    <div className="text-[9px] font-black uppercase tracking-widest text-white/70">{f.h}</div>
                    <div className="text-lg">{f.i}</div>
                    <div className="text-xs font-black">{f.t}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Colored Status Flag Counters preview */}
        <div className="mt-6 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.15)] p-4" data-testid="dp-status-flags">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] mb-2">
            List-page top-strip preview · scan status distribution in half a second
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { c: "#F5B93F", label: "Pending", n: 42 },
              { c: "#FF6A2B", label: "In Progress", n: 18 },
              { c: "#3B82F6", label: "Needs Photos", n: 7 },
              { c: "#22C6A6", label: "Completed", n: 128 },
              { c: "#B12FBB", label: "Parts Ordered", n: 11 },
              { c: "#E64C5A", label: "Emergency", n: 3 },
              { c: "#8B93A7", label: "Archived", n: 619 },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2 px-3 h-9 rounded-full border-2 hover:bg-slate-50 transition-colors cursor-pointer"
                style={{ borderColor: s.c + "44" }}>
                <span className="h-2 w-4 rounded-sm flex-shrink-0" style={{ background: s.c }}/>
                <span className="text-xs font-bold text-[#0F1F3A]">{s.label}</span>
                <span className="text-xs font-black" style={{ color: s.c }}>{s.n}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-white/40 text-xs font-medium">
            Preview only · nothing here is wired to real data · <span className="text-[#00C4CC]">/admin/design-preview</span>
          </p>
        </div>
      </div>
    </div>
    </DesignPreviewShell>
  );
}

/* ── SubComponents ─────────────────────────────────────────── */

function WorkflowArc() {
  const phases = [
    { label: "Received", count: 47, color: "#8B93A7", done: true },
    { label: "Scheduled", count: 32, color: "#F5B93F", done: true },
    { label: "Assigned", count: 24, color: "#FF6A2B", done: true },
    { label: "Photos In", count: 18, color: "#B12FBB", done: true, active: true },
    { label: "Completed", count: 128, color: "#22C6A6", done: false },
    { label: "Rebilled", count: 92, color: "#3B82F6", done: false },
    { label: "Paid", count: 78, color: "#00C4CC", done: false },
  ];
  return (
    <div className="flex items-start justify-between gap-1 overflow-x-auto pb-1">
      {phases.map((p, i) => (
        <React.Fragment key={p.label}>
          <div className="flex flex-col items-center min-w-[80px]">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center relative ${
              p.active ? "ring-4 ring-offset-2" : ""
            }`} style={{ backgroundColor: p.color, ...(p.active ? { boxShadow: `0 0 0 4px ${p.color}22` } : {}) }}>
              <span className="text-white font-black text-sm">{p.count}</span>
              {p.done && !p.active && <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[#22C6A6] flex items-center justify-center text-white text-[10px] font-black">✓</span>}
            </div>
            <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-center" style={{ color: p.active ? p.color : "#8B93A7" }}>
              {p.label}
            </div>
          </div>
          {i < phases.length - 1 && (
            <div className="flex-1 h-0.5 mt-6 rounded-full min-w-[16px]"
              style={{ background: p.done ? `linear-gradient(to right, ${p.color}, ${phases[i + 1].color})` : "#e5e7eb" }}/>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function StatusDonut() {
  // SVG donut using stroke-dasharray for slices
  const data = [
    { c: "#F5B93F", pct: 5 },   // Pending
    { c: "#FF6A2B", pct: 2 },   // In Progress
    { c: "#3B82F6", pct: 1 },   // Needs Photos
    { c: "#22C6A6", pct: 15 },  // Completed
    { c: "#E64C5A", pct: 0.5 }, // Emergency
    { c: "#8B93A7", pct: 76.5 },// Archived
  ];
  const r = 60, C = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width="180" height="180" viewBox="-90 -90 180 180" className="-rotate-90">
      <circle r={r} fill="none" stroke="#f1f5f9" strokeWidth="18"/>
      {data.map((d, i) => {
        const len = (d.pct / 100) * C;
        const el = (
          <circle key={i} r={r} fill="none" stroke={d.c} strokeWidth="18"
            strokeDasharray={`${len} ${C}`} strokeDashoffset={-offset}/>
        );
        offset += len;
        return el;
      })}
      <text x="0" y="0" textAnchor="middle" dy="0.35em" className="rotate-90" transform="rotate(90)"
        fill="#0F1F3A" fontSize="24" fontWeight="900">
        817
      </text>
    </svg>
  );
}
